<template>
  <div class="mb-10">
    <!-- header -->
    <div class="d-flex align-center">
      <div>
        <h2>指導計画</h2>
        <div class="text-caption text-grey-darken-1 mt-1">
          作成・提出・承認状況の確認からExcel出力まで、この画面で行います。
        </div>
      </div>
      <v-spacer />
      <v-btn
        v-if="canManageSetting"
        @click="openSetting"
        class="mr-2"
        color="primary"
        variant="outlined"
      >
        <v-icon start>mdi-cog</v-icon>
        帳票設定
      </v-btn>
      <v-btn @click="openCreateDialog" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        新規作成
      </v-btn>
    </div>

    <!-- status tabs -->
    <v-tabs v-model="tab" color="primary" class="mt-4" :disabled="loading">
      <v-tab v-for="statusTab in statusTabs" :key="statusTab.value" :value="statusTab.value">
        {{ statusTab.title }}
        <v-chip class="ml-2" size="x-small" variant="flat" color="grey-lighten-2">
          {{ statusCount(statusTab.countKey) }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <!-- filters -->
    <v-card variant="flat" class="mt-4 pa-2" color="grey-lighten-4">
      <v-container>
        <v-row align="center">
          <v-col cols="3">
            <v-text-field
              v-model="searchFilters.keyword"
              label="帳票名・クラス・作成者で検索"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              color="primary"
              :disabled="loading"
              @keyup.enter="searchFromFirstPage"
            ></v-text-field>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="searchFilters.planType"
              :items="planTypeItems"
              label="帳票種別"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              :disabled="loading"
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="searchFilters.classId"
              :items="classItems"
              label="クラス"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              :disabled="loading"
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="searchFilters.businessYear"
              :items="businessYearItems"
              label="年度"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              :disabled="loading"
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="searchFilters.authorUserNurseryId"
              :items="authorItems"
              label="作成者"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              :disabled="loading"
            ></v-select>
          </v-col>
          <v-col cols="1">
            <v-btn @click="searchFromFirstPage" :loading="loading" color="primary" variant="flat">
              検索
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- applied filter chips -->
    <div v-if="appliedFilters.length > 0" class="mt-3 d-flex align-center flex-wrap ga-2">
      <v-chip
        v-for="applied in appliedFilters"
        :key="applied.key"
        closable
        size="small"
        variant="outlined"
        color="primary"
        @click:close="clearFilter(applied.key)"
      >
        {{ applied.label }}
      </v-chip>
      <v-btn @click="clearAllFilters" variant="text" size="small" color="primary">
        すべて解除
      </v-btn>
    </div>

    <!-- summary / bulk action -->
    <div class="mt-4 d-flex align-center">
      <h5>計{{ listResponse ? listResponse.count : 0 }}件</h5>
      <v-spacer />
      <div v-if="exportPolling" class="d-flex align-center mr-3">
        <v-progress-circular indeterminate size="18" width="2" color="primary" class="mr-2" />
        <span class="text-caption text-grey-darken-1">Excelを作成しています...</span>
      </div>
      <v-btn
        v-if="selectedPlanIds.length > 0"
        @click="bulkExportExcel"
        :loading="exporting"
        :disabled="exportPolling"
        color="primary"
        variant="flat"
      >
        <v-icon start>mdi-microsoft-excel</v-icon>
        選択した帳票をExcel出力（{{ selectedPlanIds.length }}件）
      </v-btn>
    </div>

    <!-- table -->
    <div class="mt-2">
      <v-table>
        <thead>
          <tr>
            <th style="width: 48px">
              <v-checkbox
                :model-value="isAllSelected"
                :indeterminate="isPartiallySelected"
                @update:model-value="toggleSelectAll"
                density="compact"
                hide-details
                color="primary"
              ></v-checkbox>
            </th>
            <th>帳票</th>
            <th>対象期間</th>
            <th>クラス</th>
            <th>作成者</th>
            <th>提出期限</th>
            <th>コメント</th>
            <th>版</th>
            <th>ステータス</th>
          </tr>
        </thead>
        <tbody v-if="listResponse">
          <tr
            v-for="plan in listResponse.data"
            :key="plan.planId"
            class="teaching-plan-row"
            @click="openPlan(plan.planId)"
          >
            <td @click.stop>
              <v-checkbox
                :model-value="selectedPlanIds.includes(plan.planId)"
                @update:model-value="toggleSelect(plan.planId)"
                density="compact"
                hide-details
                color="primary"
              ></v-checkbox>
            </td>
            <td>
              <!--
                行クリックだけだとキーボードで帳票を開けない。
                帳票名をリンクにしてタブ順に載せる (行クリックはそのまま残す)。
              -->
              <a
                class="tp-plan-link"
                :href="`/teaching-plan/${plan.planId}`"
                @click.stop.prevent="openPlan(plan.planId)"
              >
                {{ planTypeLabels[plan.planType] }}
              </a>
              <div v-if="plan.templateName" class="text-caption text-grey-darken-1">
                {{ plan.templateName }}
              </div>
            </td>
            <td>{{ periodLabel(plan) }}</td>
            <td>{{ plan.className || "園全体" }}</td>
            <td>{{ plan.authorName }}</td>
            <td>
              <template v-if="plan.deadlineDate">
                <span :class="{ 'deadline-alert': isDeadlineAlert(plan) }">
                  {{ formatDate(plan.deadlineDate) }}
                </span>
                <div v-if="isDeadlineOverdue(plan)" class="deadline-overdue">期限超過</div>
              </template>
              <span v-else>—</span>
            </td>
            <td>
              <v-chip
                v-if="plan.unresolvedCommentCount > 0"
                size="small"
                variant="flat"
                color="warning"
              >
                {{ plan.unresolvedCommentCount }}
              </v-chip>
              <span v-else>—</span>
            </td>
            <td>第{{ plan.version }}版</td>
            <td>
              <div
                class="status-badge"
                :style="{
                  backgroundColor: statusStyles[plan.status].background,
                  color: statusStyles[plan.status].color,
                }"
              >
                {{ statusStyles[plan.status].label }}
              </div>
              <div v-if="plan.pendingApproverName" class="text-caption text-grey-darken-1 mt-1">
                {{ plan.pendingApproverName }}が承認待ち
              </div>
            </td>
          </tr>
          <tr v-if="listResponse.data.length === 0">
            <td colspan="9" class="text-center text-grey py-6">
              該当する指導計画がありません。
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-pagination
        class="mt-6"
        v-if="listResponse && listResponse.count > searchFilters.limit"
        v-model="page"
        :length="Math.ceil(listResponse.count / searchFilters.limit)"
        :disabled="loading"
        active-color="primary"
      ></v-pagination>
    </div>

    <!-- create dialog -->
    <v-dialog v-model="createDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>指導計画の新規作成</v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="4">帳票種別</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.planType"
                  :items="createPlanTypeItems"
                  variant="outlined"
                  density="compact"
                  hide-details
                  color="primary"
                  :disabled="creating"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-2">
              <v-col cols="4">年度</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.businessYear"
                  :items="createBusinessYearItems"
                  variant="outlined"
                  density="compact"
                  hide-details
                  color="primary"
                  :disabled="creating"
                ></v-select>
              </v-col>
            </v-row>
            <v-row v-if="createInput.planType !== 'overall'" align="center" class="mt-2">
              <v-col cols="4">クラス</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.classId"
                  :items="createClassItems"
                  variant="outlined"
                  density="compact"
                  hide-details
                  color="primary"
                  :disabled="creating"
                  no-data-text="担当クラスがありません"
                ></v-select>
                <!-- 主任以上は園の全クラスが対象なので「クラスが無い」、一般職員は「担任が未設定」が原因。
                     担任は施設設定ではなく職員管理で設定するため、権限ごとに正しい誘導先を出す -->
                <div
                  v-if="createClassItems.length === 0 && hasFullClassScope"
                  class="text-caption text-error mt-1"
                >
                  {{ createInput.businessYear }}年度のクラスがありません。施設設定 > クラス設定で{{
                    createInput.businessYear
                  }}年度のクラスを作成してください。
                </div>
                <div v-else-if="createClassItems.length === 0" class="text-caption text-error mt-1">
                  {{ createInput.businessYear }}年度に担当しているクラスがありません。園の管理者に、職員管理で担任クラスの設定を依頼してください。
                </div>
                <div v-else-if="!hasFullClassScope" class="text-caption text-grey-darken-1 mt-1">
                  自分が担当しているクラスのみ作成できます。
                </div>
              </v-col>
            </v-row>
            <v-row v-if="createInput.planType === 'monthly'" align="center" class="mt-2">
              <v-col cols="4">対象月</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.month"
                  :items="monthItems"
                  variant="outlined"
                  density="compact"
                  hide-details
                  suffix="月"
                  color="primary"
                  :disabled="creating"
                ></v-select>
              </v-col>
            </v-row>
            <v-row v-if="createInput.planType === 'weekly'" align="center" class="mt-2">
              <v-col cols="4">対象期間</v-col>
              <v-col cols="8">
                <!--
                  DatePicker に幅指定がなく、横並びにすると「開始日」「終了日」の
                  ラベルが読めないところまで縮む。行を分けて幅を確保する。
                -->
                <div class="tp-period-range">
                  <date-picker v-model="createInput.periodStartDate" label="開始日"></date-picker>
                  <span class="tp-period-range__sep">〜</span>
                  <date-picker v-model="createInput.periodEndDate" label="終了日"></date-picker>
                </div>
                <div class="text-caption text-grey-darken-1 mt-1">
                  曜日の制約はありません。開始日と終了日を同じ日にすると日案になります。
                </div>
                <div v-if="periodRangeError" class="text-caption text-error mt-1">
                  {{ periodRangeError }}
                </div>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-2">
              <v-col cols="4">作成者</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.authorUserNurseryId"
                  :items="createAuthorItems"
                  :loading="loadingAuthors"
                  :disabled="creating || loadingAuthors"
                  variant="outlined"
                  density="compact"
                  hide-details
                  color="primary"
                  no-data-text="選択できる職員がいません"
                ></v-select>
                <!-- 一般職員は共通アカウントで作業する園が多いため、午睡表の記録者と同様に職員を選ぶ。
                     対象期間の開始日時点で退職している職員と非表示の職員は候補に出さない -->
                <div
                  v-if="!loadingAuthors && createAuthorItems.length === 0"
                  class="text-caption text-error mt-1"
                >
                  選択できる職員がいません。職員管理で職員を登録してください。
                </div>
                <div v-else class="text-caption text-grey-darken-1 mt-1">
                  承認・差戻しの通知は作成者に届きます。
                </div>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-2">
              <v-col cols="4">帳票レイアウト</v-col>
              <v-col cols="8">
                <v-select
                  v-model="createInput.templateId"
                  :items="createTemplateItems"
                  :loading="loadingTemplates"
                  :disabled="creating || loadingTemplates"
                  variant="outlined"
                  density="compact"
                  hide-details
                  color="primary"
                  no-data-text="公開済みのレイアウトがありません"
                ></v-select>
                <!--
                  候補ゼロだとセレクトが空のまま何も言わず、作成できない理由が
                  分からない (開かないと no-data-text も読めない)
                -->
                <div
                  v-if="!loadingTemplates && createTemplateItems.length === 0"
                  class="text-caption text-error mt-1"
                >
                  {{ planTypeLabels[createInput.planType] }}に公開済みのレイアウトがありません。帳票設定で公開してください。
                </div>
                <div v-else-if="hasUnpublishedTemplate" class="text-caption text-grey-darken-1 mt-1">
                  未公開のレイアウトは選択できません。帳票設定で公開してください。
                </div>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-2">
              <v-col cols="4">作成方法</v-col>
              <v-col cols="8">
                <v-checkbox
                  v-model="createInput.carryFromUpper"
                  label="上位計画から転記して作成する"
                  density="compact"
                  hide-details
                  color="primary"
                  :disabled="creating || createInput.planType === 'overall'"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="createDialog = false" :disabled="creating" variant="text">
            キャンセル
          </v-btn>
          <v-btn
            @click="createPlan"
            :loading="creating"
            :disabled="!canCreate"
            color="primary"
            variant="flat"
          >
            作成
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import ApiBatchStreamLog from "@/api/ApiBatchStreamLog";
import ApiClass from "@/api/ApiClass";
import ApiNurseryUser from "@/api/ApiNurseryUser";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import DatePicker from "@/components/atoms/DatePicker.vue";
import LocalDataService from "@/service/LocalDataService";
import CommonUtil from "@/utils/CommonUtil";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { confirmInfo, notifyError, notifyWarning } from "@/utils/TeachingPlanDialog";
import {
  bulkExportLimit,
  createPlanTypeItems,
  exportPollIntervalMs,
  exportPollMaxCount,
  deadlineAlertStatuses,
  planTypeItems,
  planTypeLabels,
  statusStyles,
  statusTabs,
} from "@/constants/teachingPlan";
import {
  BatchStreamLogResponse,
  MeNurserySchoolResponse,
  NurseryClassResponse,
  NurseryTeachingPlanListItem,
  NurseryTeachingPlanListResponse,
  NurseryTeachingPlanListTabType,
  NurseryTeachingPlanTemplateListItem,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import { NurseryUserResponse } from "chaild-api/src/component/nursery/user/types";

/** 一覧フィルタのチップ表示に使うキー */
type FilterKey = "keyword" | "planType" | "classId" | "businessYear" | "authorUserNurseryId";

export default defineComponent({
  name: "TeachingPlanList",

  components: { DatePicker },

  async mounted() {
    await Promise.all([this.listClasses(), this.listAuthors()]);
    await this.listPlans();
  },

  beforeUnmount() {
    this.stopExportPolling();
  },

  data() {
    // CommonUtil.getFiscalYear() は文字列を返す
    const currentBusinessYear = Number(CommonUtil.getFiscalYear());
    return {
      loading: false,
      /** 一覧取得の後着応答を捨てるためのトークン */
      listToken: 0,
      creating: false,
      exporting: false,
      createDialog: false,
      loadingTemplates: false,
      templates: [] as NurseryTeachingPlanTemplateListItem[],
      /** 新規作成の作成者候補 (対象期間の開始日時点で在籍している職員) */
      createStaffs: [] as NurseryUserResponse[],
      loadingAuthors: false,
      /** 作成者候補の後着応答を捨てるためのトークン */
      createStaffsToken: 0,
      // 一括Excel出力の完了待ち
      exportPolling: false,
      exportPollTimer: null as number | null,
      exportPollCount: 0,
      exportTargetCreatedAt: null as string | null,
      tab: "all" as NurseryTeachingPlanListTabType,
      listResponse: null as NurseryTeachingPlanListResponse | null,
      selectedPlanIds: [] as string[],
      classes: [] as NurseryClassResponse[],
      authors: [] as { userId: number; name: string }[],
      searchFilters: {
        keyword: "" as string,
        planType: "" as NurseryTeachingPlanType | "",
        classId: null as number | null,
        businessYear: null as number | null,
        authorUserNurseryId: null as number | null,
        skip: 0,
        limit: 20,
      },
      createInput: {
        planType: "monthly" as NurseryTeachingPlanType,
        businessYear: currentBusinessYear,
        classId: null as number | null,
        month: dayjs().month() + 1,
        periodStartDate: "" as string,
        periodEndDate: "" as string,
        templateId: null as string | null,
        carryFromUpper: false,
        authorUserNurseryId: null as number | null,
      },
      // 定数
      statusTabs,
      statusStyles,
      planTypeItems,
      planTypeLabels,
      createPlanTypeItems,
      monthItems: Array.from({ length: 12 }, (_, i) => i + 1),
    };
  },

  watch: {
    tab() {
      this.searchFromFirstPage();
    },
    /** ダイアログを開いたとき・対象期間が変わったときに作成者候補を引き直す (退職判定の基準日が変わる) */
    createStaffsKey(key: string) {
      if (key) {
        this.listCreateStaffs();
      }
    },
    /** 帳票種別を変えたらレイアウトの選択肢を引き直す */
    "createInput.planType"() {
      this.createInput.templateId = null;
      this.listTemplates();
    },
    /** 年度を変えると選べるクラスが変わるため、候補から外れた選択は外す */
    "createInput.businessYear"() {
      if (
        this.createInput.classId !== null &&
        !this.createClassItems.some((item) => item.value === this.createInput.classId)
      ) {
        this.createInput.classId = this.defaultClassId();
      }
    },
    /** 開始日より前の終了日は自動で開始日に寄せる (1日=日案) */
    "createInput.periodStartDate"(value: string) {
      if (!value) {
        return;
      }
      if (!this.createInput.periodEndDate || this.createInput.periodEndDate < value) {
        this.createInput.periodEndDate = value;
      }
    },
  },

  computed: {
    /** 年度の選択肢は当年度を基準に前後2年 */
    businessYearItems(): { title: string; value: number | null }[] {
      return [
        { title: "すべて", value: null },
        ...this.createBusinessYearItems,
      ];
    },
    createBusinessYearItems(): { title: string; value: number }[] {
      const current = Number(CommonUtil.getFiscalYear());
      return [current + 1, current, current - 1, current - 2].map((year) => ({
        title: `${year}年度`,
        value: year,
      }));
    },
    /** 一覧のクラスフィルタは全年度のクラスを出すため、同名クラスを見分けられるよう年度を添える */
    classItems(): { title: string; value: number | null }[] {
      return [
        { title: "すべて", value: null },
        ...this.classes.map((item) => ({
          title: this.classLabel(item),
          value: item.classId,
        })),
      ];
    },
    /** 主任以上は園内すべて、一般ユーザーは担任クラスのみ (サーバの判定と同じ) */
    hasFullClassScope(): boolean {
      const nursery = this.currentSchool;
      return nursery ? nursery.isAdmin || nursery.isDirector || nursery.isChief : false;
    },
    /**
     * 一般ユーザーの担任クラスID (現存するもの)。
     * me の `class` は主担当1件だけで複数クラス担当に足りないため `classes` を使う。
     *
     * 論理削除されたクラスは `classes` に含まれないため、サーバの閲覧判定
     * (getHomeroomClassIds) とは集合が一致しない。ここは「これから作れるクラス」の
     * 用途なので削除済みが出ないのが正しい。既存帳票の閲覧・編集可否は
     * 一覧のスコープと詳細の permissions に従う。
     */
    homeroomClassIds(): number[] {
      const nursery = this.currentSchool;
      if (!nursery || !Array.isArray(nursery.classes)) {
        return [];
      }
      return nursery.classes.map((item) => item.classId);
    },
    /**
     * 新規作成で選べるクラス。
     * - 対象年度のクラスのみ (職員は過年度のクラスにも紐付いたまま残るため、
     *   絞らないと「2026年度の帳票に2025年度のクラス」を選べてしまう。
     *   年度未設定の古いクラスは判定できないので残す)
     * - 一般ユーザーは担任クラスのみ (サーバも担任クラス以外は権限エラーになる)
     * - 「園全体」は主任以上だけが作れる
     */
    createClassItems(): { title: string; value: number | null }[] {
      const inBusinessYear = this.classes.filter(
        (item) => item.year === null || item.year === this.createInput.businessYear
      );
      const selectable = this.hasFullClassScope
        ? inBusinessYear
        : inBusinessYear.filter((item) => this.homeroomClassIds.includes(item.classId));
      // 「園全体」は overall 専用でクラス選択欄自体が出ないため選択肢に入れない
      // (非 overall の classId なしはサーバも400を返す組み合わせで、選ぶと作成ボタンが
      //  理由の出ない無効状態になる行き止まりだった)
      return selectable.map((item) => ({
        title: item.className,
        value: item.classId as number | null,
      }));
    },
    authorItems(): { title: string; value: number | null }[] {
      return [
        { title: "すべて", value: null },
        ...this.authors.map((item) => ({ title: item.name, value: item.userId })),
      ];
    },
    /** ログインユーザーの保育園ユーザーID (作成者の初期値) */
    myUserId(): number | null {
      return this.currentSchool ? this.currentSchool.userId : null;
    },
    /**
     * 作成者候補の在籍判定に使う基準日 = 対象期間の開始日。
     * 午睡表の記録者と同様に、この日時点で退職している職員はAPI側で除外される
     * (今日で判定すると、来月の帳票の作成者に今月末で退職する職員を選べてしまう)。
     * 週案で開始日が未入力のうちは今日で判定する。
     */
    createTargetDate(): string {
      const { planType, businessYear, month, periodStartDate } = this.createInput;
      if (planType === "weekly") {
        return periodStartDate || dayjs().format("YYYY-MM-DD");
      }
      if (planType === "monthly") {
        // 年度は4月始まりなので1〜3月は翌暦年
        const year = month >= 4 ? businessYear : businessYear + 1;
        return `${year}-${String(month).padStart(2, "0")}-01`;
      }
      return `${businessYear}-04-01`;
    },
    /** 作成者候補の取得条件。ダイアログを閉じている間は空にして無駄な取得をしない */
    createStaffsKey(): string {
      return this.createDialog ? this.createTargetDate : "";
    },
    /** 新規作成で選べる作成者。非表示の職員は候補に出さない (午睡表の記録者と同方式) */
    createAuthorItems(): { title: string; value: number }[] {
      return this.createStaffs
        .filter((staff) => !staff.isHidden)
        .map((staff) => ({
          title: `${staff.lastName} ${staff.firstName}`,
          value: staff.userId,
        }));
    },
    /** 適用中フィルタのチップ */
    appliedFilters(): { key: FilterKey; label: string }[] {
      const applied: { key: FilterKey; label: string }[] = [];
      if (this.searchFilters.keyword) {
        applied.push({ key: "keyword", label: `キーワード: ${this.searchFilters.keyword}` });
      }
      if (this.searchFilters.planType) {
        applied.push({
          key: "planType",
          label: `帳票: ${this.planTypeLabels[this.searchFilters.planType]}`,
        });
      }
      if (this.searchFilters.classId !== null) {
        const target = this.classes.find((c) => c.classId === this.searchFilters.classId);
        applied.push({ key: "classId", label: `クラス: ${target ? this.classLabel(target) : ""}` });
      }
      if (this.searchFilters.businessYear !== null) {
        applied.push({
          key: "businessYear",
          label: `年度: ${this.searchFilters.businessYear}年度`,
        });
      }
      if (this.searchFilters.authorUserNurseryId !== null) {
        const target = this.authors.find(
          (a) => a.userId === this.searchFilters.authorUserNurseryId
        );
        applied.push({ key: "authorUserNurseryId", label: `作成者: ${target ? target.name : ""}` });
      }
      return applied;
    },
    isAllSelected(): boolean {
      const plans = this.listResponse ? this.listResponse.data : [];
      return plans.length > 0 && plans.every((p) => this.selectedPlanIds.includes(p.planId));
    },
    isPartiallySelected(): boolean {
      return this.selectedPlanIds.length > 0 && !this.isAllSelected;
    },
    /** ログイン中の園での自分の情報 */
    currentSchool(): MeNurserySchoolResponse | null {
      const user = LocalDataService.getUser();
      const nurseryId = LocalDataService.getNurseryId();
      if (!user || !nurseryId) {
        return null;
      }
      return user.nurserySchools.find((school) => school.nurseryId === nurseryId) || null;
    },
    /** 帳票設定 (レイアウト・転記ルール・承認者) は管理者・施設長のみ */
    canManageSetting(): boolean {
      return this.currentSchool
        ? this.currentSchool.isAdmin || this.currentSchool.isDirector
        : false;
    },
    /** 公開済みのレイアウトのみ帳票作成に使える */
    publishedTemplates(): NurseryTeachingPlanTemplateListItem[] {
      return this.templates.filter((template) => template.version !== null);
    },
    createTemplateItems(): { title: string; value: string }[] {
      return this.publishedTemplates.map((template) => ({
        title: template.name,
        value: template.templateId,
      }));
    },
    hasUnpublishedTemplate(): boolean {
      return this.templates.some((template) => template.version === null);
    },
    /** 週案の期間指定エラー (未入力・逆転) */
    periodRangeError(): string {
      if (this.createInput.planType !== "weekly") {
        return "";
      }
      // 未入力のままだと「作成」が押せない理由が出ないため、ここで理由にする
      if (!this.createInput.periodStartDate || !this.createInput.periodEndDate) {
        return "開始日と終了日を指定してください。";
      }
      if (this.createInput.periodEndDate < this.createInput.periodStartDate) {
        return "終了日は開始日以降を指定してください。";
      }
      // 年度は転記元の解決や一覧の絞り込みに効くため、開始日の属する年度と揃える
      // (年度をまたぐ週は開始日基準。例: 3/29〜4/4 は前年度)
      const startBusinessYear = Number(
        CommonUtil.getFiscalYear(this.createInput.periodStartDate)
      );
      if (startBusinessYear !== this.createInput.businessYear) {
        return `開始日は${this.createInput.businessYear}年度（${this.createInput.businessYear}年4月〜${
          this.createInput.businessYear + 1
        }年3月）の日付を指定してください。`;
      }
      return "";
    },
    canCreate(): boolean {
      if (this.createInput.templateId === null) {
        return false;
      }
      if (this.createInput.authorUserNurseryId === null) {
        return false;
      }
      if (this.createInput.planType === "weekly") {
        if (!this.createInput.periodStartDate || !this.createInput.periodEndDate) {
          return false;
        }
        if (this.periodRangeError) {
          return false;
        }
      }
      if (this.createInput.planType === "overall") {
        return true;
      }
      return this.createInput.classId !== null;
    },
    page: {
      get(): number {
        if (this.searchFilters.skip === 0) {
          return 1;
        }
        return Math.floor(this.searchFilters.skip / this.searchFilters.limit) + 1;
      },
      set(value: number) {
        this.searchFilters.skip = (value - 1) * this.searchFilters.limit;
        this.listPlans();
      },
    },
  },

  methods: {
    statusCount(key: "all" | "awaitingApproval" | "returned" | "draft" | "approved"): number {
      return this.listResponse ? this.listResponse.statusCounts[key] : 0;
    },
    periodLabel(plan: NurseryTeachingPlanListItem): string {
      return TeachingPlanUtil.periodLabel(plan);
    },
    isDeadlineAlert(plan: NurseryTeachingPlanListItem): boolean {
      return deadlineAlertStatuses.includes(plan.status);
    },
    /** 未提出のまま期限日を過ぎているか (X-5 の超過強調) */
    isDeadlineOverdue(plan: NurseryTeachingPlanListItem): boolean {
      if (!plan.deadlineDate || !this.isDeadlineAlert(plan)) {
        return false;
      }
      return dayjs(plan.deadlineDate).isBefore(dayjs().startOf("day"));
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("YYYY/MM/DD");
    },
    openPlan(planId: string) {
      this.$router.push({ path: `/teaching-plan/${planId}` });
    },
    openSetting() {
      this.$router.push({ path: "/teaching-plan/setting" });
    },
    toggleSelect(planId: string) {
      if (this.selectedPlanIds.includes(planId)) {
        this.selectedPlanIds = this.selectedPlanIds.filter((id) => id !== planId);
      } else {
        this.selectedPlanIds.push(planId);
      }
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedPlanIds = [];
        return;
      }
      this.selectedPlanIds = this.listResponse ? this.listResponse.data.map((p) => p.planId) : [];
    },
    clearFilter(key: FilterKey) {
      if (key === "keyword") {
        this.searchFilters.keyword = "";
      } else if (key === "planType") {
        this.searchFilters.planType = "";
      } else {
        this.searchFilters[key] = null;
      }
      this.searchFromFirstPage();
    },
    clearAllFilters() {
      this.searchFilters.keyword = "";
      this.searchFilters.planType = "";
      this.searchFilters.classId = null;
      this.searchFilters.businessYear = null;
      this.searchFilters.authorUserNurseryId = null;
      this.searchFromFirstPage();
    },
    async searchFromFirstPage() {
      this.searchFilters.skip = 0;
      await this.listPlans();
    },
    async listPlans() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) {
        return;
      }
      // ページャ連打などで応答順が前後しても、最後の要求だけを画面に反映する
      this.listToken += 1;
      const token = this.listToken;
      this.loading = true;
      // ページを跨いだ選択は保持しない (一括出力の対象を画面表示と一致させる)
      this.selectedPlanIds = [];
      const response = await ApiTeachingPlan.listPlans({
        nurseryId,
        tab: this.tab,
        planType: this.searchFilters.planType || undefined,
        // 契約上 classId はエンコード済み文字列なので、境界で型を揃える
        classId:
          this.searchFilters.classId !== null ? String(this.searchFilters.classId) : null,
        businessYear: this.searchFilters.businessYear,
        authorUserNurseryId: this.searchFilters.authorUserNurseryId,
        keyword: this.searchFilters.keyword || undefined,
        limit: this.searchFilters.limit,
        skip: this.searchFilters.skip,
      }).catch(() => null);
      if (token !== this.listToken) {
        return;
      }
      if (response) {
        this.listResponse = response;
      }
      this.loading = false;
    },
    classLabel(item: NurseryClassResponse): string {
      return TeachingPlanUtil.classLabel(item);
    },
    async listClasses() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) {
        return;
      }
      // クラス一覧APIは配列をそのまま返す ({ classes } で包まれていない)
      const response: unknown = await ApiClass.listClass({ nurseryId }).catch(() => null);
      if (Array.isArray(response)) {
        this.classes = response as NurseryClassResponse[];
        return;
      }
      // 取得失敗を「クラスが無い」と誤案内しない (新規作成の文言は施設設定の確認を促すため)
      notifyError(
        "クラス一覧を読み込めませんでした",
        "通信状況を確認して、ページを再読み込みしてください。"
      );
    },
    async listAuthors() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) {
        return;
      }
      const users = await ApiNurseryUser.listStaffs(nurseryId, {}).catch(() => []);
      this.authors = users.map((user) => ({
        userId: user.userId,
        name: `${user.lastName} ${user.firstName}`,
      }));
    },
    /**
     * 新規作成の作成者候補を引く。
     * 午睡表の記録者と同じスタッフ一覧APIに対象期間の開始日を渡し、退職者はAPI側で除外する
     * (非表示の職員は createAuthorItems で除外)。
     */
    async listCreateStaffs() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) {
        return;
      }
      const token = ++this.createStaffsToken;
      this.loadingAuthors = true;
      const staffs = await ApiNurseryUser.listStaffs(nurseryId, {
        date: this.createTargetDate,
      }).catch(() => null);
      // 対象期間を素早く切り替えると応答が前後する。古い基準日の候補で上書きしない
      if (token !== this.createStaffsToken) {
        return;
      }
      this.loadingAuthors = false;
      if (staffs === null) {
        notifyError(
          "職員一覧を読み込めませんでした",
          "通信状況を確認して、ページを再読み込みしてください。"
        );
        return;
      }
      this.createStaffs = staffs;
      // 選択中の作成者が候補から外れたら (退職・非表示)、ログインユーザーを既定に戻す。
      // ログインユーザー自身が候補にいない (共通アカウントが非表示など) ときは未選択にして選ばせる
      const selectable = this.createAuthorItems.map((item) => item.value);
      const selected = this.createInput.authorUserNurseryId;
      if (selected === null || !selectable.includes(selected)) {
        this.createInput.authorUserNurseryId =
          this.myUserId !== null && selectable.includes(this.myUserId) ? this.myUserId : null;
      }
    },
    /** 帳票種別に対応するレイアウトを引く (このAPIが標準レイアウトの遅延シードも行う) */
    async listTemplates() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) {
        return;
      }
      const planType = this.createInput.planType;
      this.loadingTemplates = true;
      const response = await ApiTeachingPlan.listTemplates({
        nurseryId,
        planType,
      }).catch(() => null);
      // 帳票種別を素早く切り替えると応答が前後する。古い種別のレイアウトで
      // 候補と既定選択を上書きすると、選んだ種別と合わないレイアウトで作成できてしまう
      if (planType !== this.createInput.planType) {
        return;
      }
      this.loadingTemplates = false;
      if (!response) {
        return;
      }
      this.templates = response.data;
      // 公開済みが1件だけなら選択の手間を省く (複数あるときは意図しないレイアウトが
      // 既定で入ったまま作成されないよう、明示的に選んでもらう)
      const published = this.templates.filter((template) => template.version !== null);
      if (this.createInput.templateId === null && published.length === 1) {
        this.createInput.templateId = published[0].templateId;
      }
    },
    /** 新規作成のクラス既定値。担任が1クラスなら選び直さずに済むようにする */
    defaultClassId(): number | null {
      const selectable = this.createClassItems.filter((item) => item.value !== null);
      if (selectable.length === 1) {
        return selectable[0].value;
      }
      const primary = this.currentSchool && this.currentSchool.class
        ? this.currentSchool.class.classId
        : null;
      return primary !== null && selectable.some((item) => item.value === primary)
        ? primary
        : null;
    },
    openCreateDialog() {
      this.createInput = {
        planType: "monthly",
        businessYear: Number(CommonUtil.getFiscalYear()),
        classId: this.defaultClassId(),
        month: dayjs().month() + 1,
        periodStartDate: "",
        periodEndDate: "",
        templateId: null,
        carryFromUpper: false,
        authorUserNurseryId: null,
      };
      this.templates = [];
      // 作成者候補は createStaffsKey の watch (ダイアログを開く→取得) で引く
      this.createDialog = true;
      this.listTemplates();
    },
    async createPlan() {
      const nurseryId = LocalDataService.getNurseryId();
      if (
        !nurseryId ||
        !this.canCreate ||
        this.createInput.templateId === null ||
        this.createInput.authorUserNurseryId === null
      ) {
        return;
      }
      this.creating = true;
      const isOverall = this.createInput.planType === "overall";
      const isWeekly = this.createInput.planType === "weekly";
      const response = await ApiTeachingPlan.createPlan({
        nurseryId,
        planType: this.createInput.planType,
        businessYear: this.createInput.businessYear,
        classId: isOverall ? null : String(this.createInput.classId),
        month: this.createInput.planType === "monthly" ? this.createInput.month : undefined,
        periodStartDate: isWeekly ? this.createInput.periodStartDate : undefined,
        periodEndDate: isWeekly ? this.createInput.periodEndDate : undefined,
        templateId: this.createInput.templateId,
        carryFromUpper: isOverall ? false : this.createInput.carryFromUpper,
        authorUserNurseryId: this.createInput.authorUserNurseryId,
      }).catch(() => null);
      this.creating = false;
      if (response) {
        this.createDialog = false;
        this.openPlan(response.plan.planId);
      }
    },
    /**
     * 一括Excel出力の完了待ち。
     * batch_stream_log には識別子が無いので、作成時の createdAt で自分の行を特定する
     * (同一秒に別の出力を始めた場合は取り違えうるが、出力ボタンは待機中は押せない)。
     */
    startExportPolling(nurseryId: number, log: BatchStreamLogResponse) {
      this.stopExportPolling();
      this.exportPolling = true;
      this.exportTargetCreatedAt = log.createdAt;
      this.exportPollCount = 0;
      this.exportPollTimer = window.setInterval(async () => {
        this.exportPollCount += 1;
        if (this.exportPollCount > exportPollMaxCount) {
          this.stopExportPolling();
          notifyWarning(
            "Excelの作成に時間がかかっています",
            "完了後にダウンロード一覧から取得してください。"
          );
          return;
        }
        const response = await ApiBatchStreamLog.listFiles({
          nurseryId,
          category: "teachingPlan",
          action: "export",
          date: log.date,
          limit: 50,
        }).catch(() => null);
        if (!response || !response.data) {
          return;
        }
        const target = (response.data as BatchStreamLogResponse[]).find(
          (item) => item.createdAt === this.exportTargetCreatedAt
        );
        if (!target || target.status === "inProgress") {
          return;
        }
        this.stopExportPolling();
        if (target.status === "completed") {
          if (target.url) {
            window.open(target.url, "_blank");
          } else if (
            await confirmInfo(
              "Excelの作成が完了しました",
              "ダウンロード一覧から取得できます。",
              "ダウンロード一覧を開く"
            )
          ) {
            this.$router.push({ path: "/download/list" });
          }
          return;
        }
        notifyError("Excelの作成に失敗しました", "時間をおいてもう一度お試しください。");
      }, exportPollIntervalMs);
    },
    stopExportPolling() {
      if (this.exportPollTimer !== null) {
        window.clearInterval(this.exportPollTimer);
        this.exportPollTimer = null;
      }
      this.exportPolling = false;
      this.exportTargetCreatedAt = null;
    },
    async bulkExportExcel() {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId || this.selectedPlanIds.length === 0) {
        return;
      }
      if (this.selectedPlanIds.length > bulkExportLimit) {
        notifyWarning(
          `一度に出力できるのは${bulkExportLimit}件までです`,
          `いま${this.selectedPlanIds.length}件が選ばれています。選択を減らしてからお試しください。`
        );
        return;
      }
      this.exporting = true;
      const response = await ApiTeachingPlan.bulkExportExcel({
        nurseryId,
        planIds: this.selectedPlanIds,
      }).catch(() => null);
      this.exporting = false;
      if (response) {
        this.selectedPlanIds = [];
        // 出力はStepFunctionsで非同期に走るため、完了まで進捗を出しながら待つ
        this.startExportPolling(nurseryId, response);
      }
    },
  },
});
</script>

<style scoped>
.teaching-plan-row {
  cursor: pointer;
}
/* キーボードで帳票を開くための導線。見た目は行の一部として馴染ませる */
.tp-plan-link {
  color: inherit;
  text-decoration: none;
}
.tp-plan-link:hover,
.tp-plan-link:focus-visible {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
.teaching-plan-row:hover {
  background-color: #f5f5f5;
}
.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
/* 日付ピッカーは縮むとラベルが読めなくなるので最低幅を持たせる */
.tp-period-range {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.tp-period-range > :deep(div) {
  flex: 1 1 150px;
  min-width: 150px;
}
.tp-period-range__sep {
  flex: none;
}
.deadline-overdue {
  /*
   * 日付と同じ行に置くと密着して「2026/07/25期限超過」と読め、
   * 余白を足すと列幅を押し広げて他の列が潰れた。行を分ける。
   */
  display: block;
  width: fit-content;
  margin-top: 2px;
  padding: 0 6px;
  border-radius: 8px;
  background-color: #c62828;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
.deadline-alert {
  color: #c62828;
  font-weight: 700;
}
:deep(.v-pagination__item--is-active .v-btn) {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
  font-weight: bold;
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
</style>
